// Streamed-model load trace — where a first load's time goes. Off unless
// the address has ?loadTrace=1; then one summary is printed to the console
// when the view has finished streaming (and window.__loadTrace() prints it
// again any time).
//
//   download  — sum of per-chunk download times (overlapping ones add up)
//   decode    — sum of per-chunk decode times in the workers
//   write     — time spent writing decoded chunks into GPU memory
//   in flight — average number of chunk jobs (download + decode) running
//   starved   — time with NO chunk job running while the view still wasn't
//               fully loaded: the scheduler had nothing to give the workers
const enabled = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('loadTrace') === '1';

const t = {
  start: 0, firstSplats: 0, done: 0,
  chunks: 0, failed: 0,
  networkBytes: 0, totalBytes: 0,
  downloadMs: 0, decodeMs: 0, queueWaitMs: 0, writeMs: 0, writeFrames: 0,
  inflight: 0, inflightArea: 0, starvedMs: 0, lastAt: 0,
};

function tick(now) {
  if (t.lastAt) {
    const dt = now - t.lastAt;
    t.inflightArea += t.inflight * dt;
    if (t.inflight === 0 && !t.done) t.starvedMs += dt;
  }
  t.lastAt = now;
}

const s = (ms) => `${(ms / 1000).toFixed(1)} s`;

function summary() {
  const end = t.done || performance.now();
  const total = end - t.start;
  return {
    'total (to fully streamed)': s(total),
    'first splats on screen': t.firstSplats ? s(t.firstSplats - t.start) : '—',
    chunks: `${t.chunks}${t.failed ? ` (+${t.failed} failed)` : ''}`,
    downloaded: `${(t.networkBytes / 1048576).toFixed(0)} MB from network, ${((t.totalBytes - t.networkBytes) / 1048576).toFixed(0)} MB from cache`,
    'download time (summed)': s(t.downloadMs),
    'decode time (summed)': s(t.decodeMs),
    'waiting for decoder (summed)': s(t.queueWaitMs),
    'GPU write time': `${s(t.writeMs)} over ${t.writeFrames} frames`,
    'avg chunk jobs in flight': (t.inflightArea / Math.max(1, total)).toFixed(2),
    'starved (no job running)': `${s(t.starvedMs)} (${Math.round((t.starvedMs / Math.max(1, total)) * 100)}%)`,
  };
}

export const loadTrace = {
  enabled,
  start() {
    if (!enabled) return;
    Object.assign(t, { start: performance.now(), firstSplats: 0, done: 0, chunks: 0, failed: 0, networkBytes: 0, totalBytes: 0, downloadMs: 0, decodeMs: 0, queueWaitMs: 0, writeMs: 0, writeFrames: 0, inflight: 0, inflightArea: 0, starvedMs: 0, lastAt: performance.now() });
  },
  jobStart() { if (!enabled || !t.start) return; tick(performance.now()); t.inflight++; },
  jobEnd(info) {
    if (!enabled || !t.start) return;
    tick(performance.now());
    t.inflight = Math.max(0, t.inflight - 1);
    if (!info) { t.failed++; return; }
    t.chunks++;
    t.networkBytes += info.networkBytes || 0;
    t.totalBytes += info.totalBytes || 0;
    t.downloadMs += info.downloadMs || 0;
    t.decodeMs += info.decodeMs || 0;
    t.queueWaitMs += info.queueWaitMs || 0;
  },
  write(ms) { if (!enabled || !t.start || t.done) return; t.writeMs += ms; t.writeFrames++; },
  firstSplats() { if (enabled && t.start && !t.firstSplats) t.firstSplats = performance.now(); },
  idle() {
    if (!enabled || !t.start || t.done || t.chunks === 0) return;
    tick(performance.now());
    t.done = performance.now();
    console.log('%c[load trace] streamed model', 'font-weight:bold');
    console.table(summary());
  },
};

if (enabled) window.__loadTrace = () => console.table(summary());
